import React, { useContext } from "react";
import { IPhoneDirItem } from "../interfaces";
import { ApplicationError } from "../models/ApplicationError";
import { IAttributes } from "../models/IAttributes";
import { getLogger } from "../services/LoggingService";
import AppContext from "./AppContextProvider";
import TokenContext from "./TokenContext";

const logger = getLogger("ApiContext");

interface IRestApiContext {
    getTenantDirectoryByQueue: (clientIdentifier: string, queueName: string) => Promise<IPhoneDirItem[]>;
    getTenantDispositionsByQueue: (clientIdentifier: string, queueName: string) => Promise<string[]>;
    updateContactAttributes: (initialContactId: string, attributes: IAttributes) => Promise<void>;
    ruleTrigger: (ruleType: string, ruleValue: string) => Promise<void>;
}
export const RestApiContext = React.createContext<IRestApiContext>(null as any);

export const RestApiContextProvider = (props: { children: any }) => {
    const {
        config: { apiUrl },
    } = useContext(AppContext);
    const { token } = useContext(TokenContext);

    /**
     * Abstracts API access, dont use directly, create helper methods.
     */
    const post = async (endpoint: string, body: object): Promise<Response> => {
        if (!token) {
            logger.error(`API call to ${endpoint} failed because CCP Token is missing`);
            throw new ApplicationError(401, "CCP Token is missing");
        }

        try {
            return await fetch(apiUrl + endpoint, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(body),
            });
        } catch (error) {
            throw new ApplicationError(0, error.message);
        }
    };

    // The local storage token is out of date and not changing with the rest of the call data
    const getTenantDirectoryByQueue = async (clientIdentifier: string, queueName: string): Promise<IPhoneDirItem[]> => {
        const body = {
            clientIdentifier,
            queueName,
        };
        const response = await post("/getPhoneDirectory", body);
        if (response.status === 200) {
            if (!response) {
                return [];
            }
            return response.json();
        } else if (response.status >= 400 && response.status < 500) {
            logger.debug("400 Error: wait for token refresh on new contact");
            return [];
        } else {
            throw new ApplicationError(response.status, await response.text());
        }
    };

    const getTenantDispositionsByQueue = async (clientIdentifier: string, queueName: string): Promise<string[]> => {
        const body = {
            clientIdentifier,
            queueName,
        };
        const response = await post("/getDispositions", body);
        if (response.status === 200) {
            if (!response) {
                return [];
            }
            return response.json();
        } else if (response.status >= 400 && response.status < 500) {
            logger.debug("400 Error: wait for token refresh on new contact");
            return [];
        } else {
            throw new ApplicationError(response.status, await response.text());
        }
    };

    const updateContactAttributes = async (initialContactId: string, attributes: IAttributes): Promise<void> => {
        const body = {
            initialContactId: initialContactId,
            attributes,
        };
        const response = await post("/updateAttributes", body);
        if (response.status !== 200) {
            throw new ApplicationError(response.status, await response.text());
        }
    };

    const ruleTrigger = async (ruleType: string, ruleValue: string): Promise<void> => {
        const body = {
            ruleType,
            ruleValue,
        };
        const response = await post("/ruleTrigger", body);
        if (response.status !== 200) {
            throw new ApplicationError(response.status, await response.text());
        }
    };

    const ctx: IRestApiContext = {
        getTenantDirectoryByQueue,
        getTenantDispositionsByQueue,
        updateContactAttributes,
        ruleTrigger,
    };

    return <RestApiContext.Provider value={ctx}>{props.children}</RestApiContext.Provider>;
};

export default RestApiContext;
